import { useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppApi } from "src/appApi";
import { profileToVariableValues, requestToVariableValues } from "src/infra/documentVariables";
import { DOCUMENT_VARIABLE_KEYS } from "@shared/entities";
import type { TSignaturePosition, TDocumentTemplate, TDocumentContent } from "@shared/entities";
import type { TProfile, TRequest } from "@shared/entities";
import type { EditorAdapterHandle } from "src/components/editor/types";

type Step = 1 | 2 | 3 | 4;

type UseUseTemplateModalParams = {
  requestId: string;
  profile?: TProfile;
  request: TRequest;
  onSuccess: () => void;
  onClose: () => void;
};

export function useUseTemplateModal({
  requestId,
  profile,
  request,
  onSuccess,
}: UseUseTemplateModalParams) {
  const appApi = useAppApi();
  const appApiRef = useRef(appApi);
  appApiRef.current = appApi;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const { t } = useTranslation("doctorTemplates");

  const [step, setStep] = useState<Step>(1);
  const [templates, setTemplates] = useState<TDocumentTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TDocumentTemplate | null>(null);
  const [editedContent, setEditedContent] = useState<TDocumentContent | null>(null);
  const [title, setTitle] = useState("");
  const [signaturePosition, setSignaturePosition] = useState<TSignaturePosition>("none");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editorHandleRef = useRef<EditorAdapterHandle | null>(null);
  const variableValues = useMemo(
    () => (profile ? profileToVariableValues(profile, request) : requestToVariableValues(request)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile?.id, request?.id],
  );
  const variableKeys = DOCUMENT_VARIABLE_KEYS;

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setError(null);
    try {
      const list = await appApiRef.current.api.doctor.templates.list();
      setTemplates(list);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("errors.loadFailed"));
    } finally {
      setTemplatesLoading(false);
    }
  }, [t]);

  const handleSelectTemplate = useCallback((template: TDocumentTemplate) => {
    setSelectedTemplate(template);
  }, []);

  const handleEditorReady = useCallback(
    (handle: EditorAdapterHandle) => {
      editorHandleRef.current = handle;
      if (profile) {
        handle.substituteVariables(variableValues);
      }
    },
    [variableValues, profile]
  );

  const handleContentChange = useCallback((content: TDocumentContent) => {
    setEditedContent(content);
  }, []);

  const goToStep2 = useCallback(() => {
    if (!selectedTemplate) return;
    setTitle(selectedTemplate.title);
    setEditedContent(selectedTemplate.content);
    setStep(2);
  }, [selectedTemplate]);

  const goToPreview = useCallback(() => {
    const content = editorHandleRef.current?.getContent() ?? editedContent;
    if (content) setEditedContent(content);
    setStep(3);
  }, [editedContent]);

  const goToStep4 = useCallback(() => {
    setStep(4);
  }, []);

  const goBack = useCallback(() => {
    setStep((s) => (s > 1 ? ((s - 1) as Step) : s));
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!selectedTemplate || !editedContent) return;
    setGenerating(true);
    setError(null);
    console.log("[useTemplate] generating", {
      templateId: selectedTemplate.id,
      templateTitle: selectedTemplate.title,
      requestId,
      contentLength: JSON.stringify(editedContent).length,
    });
    try {
      await appApiRef.current.api.doctor.generateDocumentFromTemplate({
        requestId,
        templateId: selectedTemplate.id,
        content: editedContent,
        title,
        signaturePosition,
      });
      // The cloud function writes the document to Firestore atomically.
      // No separate addDocumentToRequest call is needed or safe here.
      onSuccessRef.current();
    } catch (err: unknown) {
      console.error("[useTemplate] generate failed", err);
      setError(err instanceof Error ? err.message : t("errors.generateFailed"));
    } finally {
      setGenerating(false);
    }
  }, [selectedTemplate, editedContent, requestId, title, signaturePosition, t]);

  return {
    step,
    templates,
    templatesLoading,
    selectedTemplate,
    title,
    signaturePosition,
    generating,
    error,
    editedContent,
    variableKeys,
    variableValues,
    loadTemplates,
    handleSelectTemplate,
    handleEditorReady,
    handleContentChange,
    setTitle,
    setSignaturePosition,
    goToStep2,
    goToPreview,
    goToStep4,
    goBack,
    handleGenerate,
  };
}
